import { z } from "zod";

import type { FilterKeys } from "../shared";
import { idSchema } from "../shared";
import { awardSchema } from "./award";
import { certificationSchema } from "./certification";
import { customSectionSchema } from "./custom-section";
import { educationSchema } from "./education";
import { experienceSchema } from "./experience";
import { hobbySchema } from "./hobby";
import { interestSchema } from "./interest";
import { mostProudOfSchema } from "./most-proud-of";
import { myMindItemSchema } from "./my-mind";
import { languageSchema } from "./language";
import { profileSchema } from "./profile";
import { projectSchema } from "./project";
import { publicationSchema } from "./publication";
import { referenceSchema } from "./reference";
import { signatureSchema } from "./signature";
import { skillSchema } from "./skill";
import { volunteerSchema } from "./volunteer";

// Schema
export const sectionSchema = z.object({
  name: z.string(),
  icon: z.string().optional(),
  columns: z.number().min(1).max(5).default(1),
  separateLinks: z.boolean().default(true),
  visible: z.boolean().default(true),
});

// Schema
export const customSchema = sectionSchema.extend({
  id: idSchema,
  items: z.array(customSectionSchema),
});

export const sectionsSchema = z.object({
  summary: sectionSchema.extend({
    id: z.literal("summary"),
    content: z.string().default(""),
  }),
  awards: sectionSchema.extend({
    id: z.literal("awards"),
    items: z.array(awardSchema),
  }),
  certifications: sectionSchema.extend({
    id: z.literal("certifications"),
    items: z.array(certificationSchema),
  }),
  education: sectionSchema.extend({
    id: z.literal("education"),
    items: z.array(educationSchema),
  }),
  experience: sectionSchema.extend({
    id: z.literal("experience"),
    items: z.array(experienceSchema),
  }),
  volunteer: sectionSchema.extend({
    id: z.literal("volunteer"),
    items: z.array(volunteerSchema),
  }),
  interests: sectionSchema.extend({
    id: z.literal("interests"),
    items: z.array(interestSchema),
  }),
  languages: sectionSchema.extend({
    id: z.literal("languages"),
    items: z.array(languageSchema),
  }),
  profiles: sectionSchema.extend({
    id: z.literal("profiles"),
    items: z.array(profileSchema),
  }),
  projects: sectionSchema.extend({
    id: z.literal("projects"),
    items: z.array(projectSchema),
  }),
  publications: sectionSchema.extend({
    id: z.literal("publications"),
    items: z.array(publicationSchema),
  }),
  hobbies: sectionSchema.extend({
    id: z.literal("hobbies"),
    items: z.array(hobbySchema),
  }),
  mostProudOf: sectionSchema.extend({
    id: z.literal("mostProudOf"),
    items: z.array(mostProudOfSchema),
  }),
  myMind: sectionSchema.extend({
    id: z.literal("myMind"),
    items: z.array(myMindItemSchema),
    ringThickness: z.number().min(1).max(5).default(4),
    showLegend: z.boolean().default(true),
    legendPlacement: z.enum(["top", "bottom", "right", "left"]).default("right"),
  }),
  references: sectionSchema.extend({
    id: z.literal("references"),
    items: z.array(referenceSchema),
  }),
  signature: sectionSchema.extend({
    id: z.literal("signature"),
    items: z.array(signatureSchema),
  }),
  skills: sectionSchema.extend({
    id: z.literal("skills"),
    items: z.array(skillSchema),
  }),
  custom: z.record(z.string(), customSchema),
});

// Detailed Types
export type Section = z.infer<typeof sectionSchema>;
export type Sections = z.infer<typeof sectionsSchema>;

export type SectionKey = "basics" | keyof Sections | `custom.${string}`;
export type SectionWithItem<T = unknown> = Sections[FilterKeys<Sections, { items: T[] }>];
export type SectionItem = SectionWithItem["items"][number];
export type CustomSectionGroup = z.infer<typeof customSchema>;

// Defaults
export const defaultSection: Section = {
  name: "",
  columns: 1,
  separateLinks: true,
  visible: true,
};

export const defaultSections: Sections = {
  summary:        { ...defaultSection, id: "summary",        name: "Summary",             icon: "article",              content: "" },
  awards:         { ...defaultSection, id: "awards",         name: "Awards",              icon: "trophy",               items: [] },
  certifications: { ...defaultSection, id: "certifications", name: "Certifications",      icon: "certificate",          items: [] },
  education:      { ...defaultSection, id: "education",      name: "Education",           icon: "graduation-cap",       items: [] },
  experience:     { ...defaultSection, id: "experience",     name: "Experience",          icon: "briefcase",            items: [] },
  volunteer:      { ...defaultSection, id: "volunteer",      name: "Volunteering",        icon: "hand-heart",           items: [] },
  interests:      { ...defaultSection, id: "interests",      name: "Interests",           icon: "sparkle",              items: [] },
  languages:      { ...defaultSection, id: "languages",      name: "Languages",           icon: "translate",            items: [] },
  profiles:       { ...defaultSection, id: "profiles",       name: "Profiles",            icon: "identification-badge", items: [] },
  projects:       { ...defaultSection, id: "projects",       name: "Projects",            icon: "folder-open",          items: [] },
  publications:   { ...defaultSection, id: "publications",   name: "Publications",        icon: "book-open",            items: [] },
  hobbies:        { ...defaultSection, id: "hobbies",        name: "Hobbies",             icon: "palette",              items: [] },
  mostProudOf:    { ...defaultSection, id: "mostProudOf",    name: "Core Strengths",      icon: "medal",                items: [] },
  myMind:         { ...defaultSection, id: "myMind",         name: "Mindset & Strengths", icon: "brain",                items: [], ringThickness: 4, showLegend: true, legendPlacement: "right" as const },
  references:     { ...defaultSection, id: "references",     name: "References",          icon: "users",                items: [] },
  signature:      { ...defaultSection, id: "signature",      name: "Signature",           icon: "pen-nib",              items: [] },
  skills:         { ...defaultSection, id: "skills",         name: "Skills",              icon: "lightning",            items: [] },
  custom: {},
};

export * from "./award";
export * from "./certification";
export * from "./custom-section";
export * from "./education";
export * from "./experience";
export * from "./hobby";
export * from "./interest";
export * from "./most-proud-of";
export * from "./my-mind";
export * from "./language";
export * from "./profile";
export * from "./project";
export * from "./publication";
export * from "./reference";
export * from "./signature";
export * from "./skill";
export * from "./volunteer";
